import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { superAdmin as api } from "@/lib/api/super";
import type {
  SuperActives,
  SuperActivity,
  SuperCohorts,
  SuperGeo,
  SuperGrowth,
  SuperOverview,
  SuperTrendPoint,
} from "@/lib/api/super";
import { useAuth } from "./useAuth";

export type {
  SuperActives,
  SuperActivity,
  SuperCohort,
  SuperCohorts,
  SuperGrowth,
  SuperTrendPoint,
  SuperActivityPoint,
  SuperGeo,
  SuperGeoAttribution,
  SuperGeoCountry,
  SuperOverview,
  SuperPeriodCounts,
  SuperRecentSignIn,
  SuperUserCounts,
} from "@/lib/api/super";

export type SuperMetricsStatus = "idle" | "loading" | "ready" | "error";

export const ACTIVITY_WINDOWS = [7, 30, 90, 365] as const;
export type ActivityWindow = (typeof ACTIVITY_WINDOWS)[number];

export interface SuperMetricsContextValue {
  status: SuperMetricsStatus;
  error: Error | null;
  overview: SuperOverview | null;
  activity: SuperActivity | null;
  geo: SuperGeo | null;
  geoUnavailable: boolean;
  cohorts: SuperCohorts | null;
  actives: SuperActives | null;
  growth: SuperGrowth | null;
  trends: readonly SuperTrendPoint[] | null;
  days: ActivityWindow;
  setDays: (days: ActivityWindow) => void;
  reload: () => Promise<void>;
}

const SuperMetricsContext = createContext<SuperMetricsContextValue>(null!);

export function useSuperMetrics(): SuperMetricsContextValue {
  return useContext(SuperMetricsContext);
}

function useSuperMetricsProvider(): SuperMetricsContextValue {
  const { user, status: authStatus } = useAuth();
  const [overview, setOverview] = useState<SuperOverview | null>(null);
  const [activity, setActivity] = useState<SuperActivity | null>(null);
  const [geo, setGeo] = useState<SuperGeo | null>(null);
  const [geoUnavailable, setGeoUnavailable] = useState(false);
  const [cohorts, setCohorts] = useState<SuperCohorts | null>(null);
  const [actives, setActives] = useState<SuperActives | null>(null);
  const [growth, setGrowth] = useState<SuperGrowth | null>(null);
  const [trends, setTrends] = useState<readonly SuperTrendPoint[] | null>(null);
  const [days, setDays] = useState<ActivityWindow>(30);
  const [status, setStatus] = useState<SuperMetricsStatus>("idle");
  const [error, setError] = useState<Error | null>(null);

  const reload = useCallback(
    () =>
      Promise.all([
        api.fetchSuperOverview(),
        api.fetchSuperActivity(days),
        api.fetchSuperGeo(days).catch(() => null),
        api.fetchCohorts().catch(() => null),
        api.fetchActives().catch(() => null),
        api.fetchGrowth().catch(() => null),
        api.fetchTrends().catch(() => null),
      ])
        .then(([o, a, g, c, ac, gr, tr]) => {
          setOverview(o);
          setActivity(a);
          setGeo(g);
          setGeoUnavailable(g === null);
          setCohorts(c);
          setActives(ac);
          setGrowth(gr);
          setTrends(tr?.series ?? null);
          setStatus("ready");
          setError(null);
        })
        .catch((e: Error) => {
          setError(e);
          setStatus("error");
        }),
    [days],
  );

  useEffect(() => {
    if (authStatus !== "authenticated" || !user?.admin) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- load on auth/filter flip
    setStatus((s) => (s === "ready" ? s : "loading"));
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStatus, user?.admin, days]);

  return useMemo(
    () => ({
      status,
      error,
      overview,
      activity,
      geo,
      geoUnavailable,
      cohorts,
      actives,
      growth,
      trends,
      days,
      setDays,
      reload,
    }),
    [
      status,
      error,
      overview,
      activity,
      geo,
      geoUnavailable,
      cohorts,
      actives,
      growth,
      trends,
      days,
      reload,
    ],
  );
}

export function SuperMetricsProvider({ children }: { children: ReactNode }) {
  const value = useSuperMetricsProvider();
  return (
    <SuperMetricsContext.Provider value={value}>
      {children}
    </SuperMetricsContext.Provider>
  );
}
